"use client"

import styled from "@emotion/styled"
import { useEffect, useMemo, useRef, useState } from "react"

export type PowerNumberStatData = {
  number: string
  numberCaption: string
  textLabel?: string
}

type Variant = "mode1" | "mode2"

/* Numbers use Narkiss Block Condensed. Only the Medium (500) weight file is
   loaded, so 700 renders as synthetic bold until a Bold woff2 is added. */
const StatNumber = styled.div<{ variant: Variant }>`
  font-size: ${({ variant }) => (variant === "mode1" ? "95px" : "80px")};
  font-weight: 700;
  line-height: ${({ variant }) => (variant === "mode1" ? "66px" : "80px")};
  color: var(--inner-heading, var(--comp-heading));

  /* Power Numbers 2 steps up to 100 on desktop (ASH-1473 / ASH-1575);
     Power Numbers 1 stays at 95 on both breakpoints. */
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: ${({ variant }) => (variant === "mode1" ? "95px" : "100px")};
  }
`

/* A currency symbol sits at half the number's size — 50 against 100 on desktop,
   40 against 80 on mobile (ASH-1575). Only currency is scaled down; "+" and
   similar affixes stay at full size. */
const Symbol = styled.span`
  font-size: 0.5em;
  line-height: 1;
`

const StatTextLabel = styled.div`
  font-size: 84px;
  font-weight: 700;
  line-height: 0.7;
  color: var(--inner-heading, var(--comp-heading));
`

const StatCaption = styled.div<{ variant: Variant }>`
  font-size: ${({ variant }) => (variant === "mode1" ? "28px" : "24px")};
  font-weight: ${({ variant }) => (variant === "mode1" ? 700 : 400)};
  line-height: ${({ variant }) => (variant === "mode1" ? "36px" : "26px")};
  color: var(--inner-heading, var(--comp-heading));

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: ${({ variant }) => (variant === "mode1" ? "28px" : "32px")};
  }
`

// --- Count-up animation ---------------------------------------------------
// Animates the numeric part of a stat from 0 to its final value once, when it
// scrolls into view. Non-numeric prefix/suffix (e.g. "+", "₪") and thousands
// separators / decimals from the source string are preserved.

const COUNT_UP_DURATION = 1200

type ParsedNumber = {
  prefix: string
  suffix: string
  target: number
  decimals: number
  hasThousands: boolean
}

function parseNumber(raw: string): ParsedNumber | null {
  const match = raw.match(/\d[\d,]*(?:\.\d+)?/)
  if (!match || match.index === undefined) return null

  const numericStr = match[0]
  const decimalPart = numericStr.split(".")[1]

  return {
    prefix: raw.slice(0, match.index),
    suffix: raw.slice(match.index + numericStr.length),
    target: parseFloat(numericStr.replace(/,/g, "")),
    decimals: decimalPart ? decimalPart.length : 0,
    hasThousands: numericStr.includes(","),
  }
}

function formatValue(value: number, decimals: number, hasThousands: boolean): string {
  const fixed = decimals > 0 ? value.toFixed(decimals) : Math.round(value).toString()
  if (!hasThousands) return fixed

  const [intPart, decPart] = fixed.split(".")
  const withSeparators = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  return decPart ? `${withSeparators}.${decPart}` : withSeparators
}

function useCountUp(raw: string) {
  const parsed = useMemo(() => parseNumber(raw), [raw])
  const ref = useRef<HTMLDivElement>(null)
  const [value, setValue] = useState(0)

  useEffect(() => {
    if (!parsed) return
    const el = ref.current
    if (!el) return

    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches

    let raf = 0

    const run = () => {
      if (prefersReduced) {
        setValue(parsed.target)
        return
      }
      const start = performance.now()
      const tick = (now: number) => {
        const t = Math.min((now - start) / COUNT_UP_DURATION, 1)
        const eased = 1 - Math.pow(1 - t, 3) // ease-out cubic
        setValue(parsed.target * eased)
        if (t < 1) raf = requestAnimationFrame(tick)
        else setValue(parsed.target)
      }
      raf = requestAnimationFrame(tick)
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect()
          run()
        }
      },
      { threshold: 0.4 },
    )
    observer.observe(el)

    return () => {
      observer.disconnect()
      cancelAnimationFrame(raf)
    }
  }, [parsed])

  const digits = parsed ? formatValue(value, parsed.decimals, parsed.hasThousands) : ""

  return {
    ref,
    prefix: parsed?.prefix ?? "",
    digits,
    suffix: parsed?.suffix ?? "",
    display: parsed ? `${parsed.prefix}${digits}${parsed.suffix}` : raw,
    isNumeric: parsed !== null,
  }
}

const CURRENCY = /^[₪$€£¥]+$/

/* Wraps a currency affix so it can be rendered at half size; anything else
   (a "+", a unit suffix) is left alone. */
function Affix({ value }: { value: string }) {
  if (!value) return null
  return CURRENCY.test(value.trim()) ? <Symbol>{value}</Symbol> : <>{value}</>
}

function PowerNumberStat({ stat, variant }: { stat: PowerNumberStatData; variant: Variant }) {
  const number = stat.number?.trim() || ""
  const { ref, prefix, digits, suffix, isNumeric } = useCountUp(number)

  // A non-numeric value typed into the number field renders at the 84px text size, no count-up (ASH-1461)
  const textValue = number && !isNumeric ? number : !number ? stat.textLabel : ""

  return (
    <div>
      {number && isNumeric ? (
        <StatNumber ref={ref} className="mlp-font-narkis-cond" variant={variant}>
          <Affix value={prefix} />
          {digits}
          <Affix value={suffix} />
        </StatNumber>
      ) : (
        textValue && <StatTextLabel className="mlp-font-narkis">{textValue}</StatTextLabel>
      )}
      {stat.numberCaption && (
        <StatCaption className="mlp-font-narkis" variant={variant}>
          {stat.numberCaption}
        </StatCaption>
      )}
    </div>
  )
}

export default PowerNumberStat
